import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import BackToTop from '@/components/feature/BackToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SectionHeading from '@/components/base/SectionHeading';
import { useSEO } from '@/hooks/useSEO';
import { siteInfo } from '@/mocks/site';
import { testimonials } from '@/mocks/testimonials';

const PRINCIPLES = [
  {
    icon: 'ri-search-eye-line',
    title: '先核实，再发布',
    text: '涉及具体个人的内容，至少经过两次公开信息交叉确认；无法确认的，宁可写成“待证实”，也不抢先下结论。',
  },
  {
    icon: 'ri-history-line',
    title: '保留更新记录',
    text: '话题页按时间顺序追加新进展，已发布的内容不会悄悄改动。读者看到的是完整过程，而不是被修剪过的结果。',
  },
  {
    icon: 'ri-scales-3-line',
    title: '观点并列呈现',
    text: '把正反双方的说法与依据分开列出，让分歧本身成为信息的一部分，而不是只挑最刺激的那一面。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '回应渠道常开',
    text: '涉事方与读者都可以申请更正。核实成立的内容会公开标注更正说明，并保留原始判断的修正记录。',
  },
];

const WORKFLOW = [
  { step: '01', title: '线索汇总', text: '从公开报道、官方声明与读者来信中收集素材，先做来源标注。' },
  { step: '02', title: '交叉核对', text: '对关键时间点与人物关系做二次确认，梳理出可读的事件时间线。' },
  { step: '03', title: '整理成稿', text: '按栏目归位，用观察视角成稿，并保留后续更新的空间。' },
  { step: '04', title: '持续跟进', text: '有新进展时追加更新，并在首页实时更新流中提示读者。' },
];

export default function About() {
  const jsonLd = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于我们 - 黑料首页',
        description: '黑料首页的品牌介绍、网站定位、发展理念与内容原则。',
        url: `${origin}/about`,
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: siteInfo.name,
        email: siteInfo.email,
        foundingDate: siteInfo.since,
        description: '以话题观察方式整理娱乐热点与网络讨论的中文资讯站点。',
        knowsLanguage: ['zh-CN'],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: origin || undefined },
          { '@type': 'ListItem', position: 2, name: '关于我们' },
        ],
      },
    ];
  }, []);

  useSEO({
    title: '关于我们 - 品牌介绍与内容原则 | 黑料首页',
    description:
      '了解黑料首页的品牌定位、发展理念与内容原则：以话题观察的方式整理娱乐热点与网络讨论，先核实再发布，保留更新记录，观点并列呈现。',
    keywords: '关于我们,黑料首页,内容原则,品牌介绍,话题观察',
    canonicalPath: '/about',
    jsonLd,
  });

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto max-w-shell px-4 py-8 md:px-6 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
            <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
              关于我们
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-700">
              {siteInfo.name} 是一个以「话题观察」为核心的中文资讯站点，自 {siteInfo.since} 年起持续整理娱乐热点与网络讨论。
              我们不生产情绪，只把碎片拼成一条你能看懂的时间线。
            </p>
          </div>
        </div>

        <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
              <div className="overflow-hidden rounded-lg border border-background-200">
                <img
                  src="https://readdy.ai/api/search-image?query=warm%20abstract%20editorial%20still%20life%20with%20layered%20coral%20red%20and%20amber%20gold%20paper%20cut%20shapes%20on%20a%20cream%20textured%20desk%2C%20soft%20film%20grain%2C%20minimal%20modern%20graphic%20art%2C%20notebook%20and%20coffee%20cup%20silhouettes%2C%20no%20text%2C%20high%20detail&width=1200&height=800&seq=hl-about-01&orientation=landscape"
                  alt="黑料首页编辑部工作场景示意图，暖色调抽象拼贴"
                  className="h-72 w-full object-cover object-top md:h-96"
                />
              </div>
              <div>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium tracking-wide text-primary-700">
                  <i className="ri-compass-3-line" aria-hidden="true" />
                  网站定位
                </span>
                <h2 className="mt-2 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-[28px]">
                  把热点事件整理成可读的时间线
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  娱乐话题的传播速度快、片段多、语境容易被切断。很多人看到的只是一个被剪短的瞬间，却很难判断前因后果。
                  我们做的事情很朴素：把公开信息按时间排列，标出每一次更新，让读者自己完成判断。
                </p>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">
                  首屏区保持高频刷新，最新动态、轮播更新、新内容持续上架，这也是越来越多读者把它设为浏览器首页的原因——
                  打开就能看到刚刚发生的事。
                </p>
                <div className="mt-6 grid grid-cols-2 gap-4">
                  {siteInfo.stats.map((stat) => (
                    <div key={stat.label} className="rounded-lg border border-background-200 bg-background-100 p-4">
                      <p className="font-heading text-2xl font-bold text-primary-700">{stat.value}</p>
                      <p className="mt-1 text-xs text-foreground-600">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <SectionHeading
              eyebrow="内容原则"
              title="我们坚持的四件事"
              description="这些原则决定了什么内容会被发布、以什么方式发布，也决定了我们如何处理争议与更正。"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {PRINCIPLES.map((item) => (
                <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-700">
                    <i className={item.icon} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <SectionHeading eyebrow="工作方式" title="一条内容从线索到成稿" />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {WORKFLOW.map((item) => (
                <div key={item.step} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <span className="font-heading text-3xl font-bold text-accent-500">{item.step}</span>
                  <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="border-b border-background-200 bg-background-100 py-14 md:py-20">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <SectionHeading
              eyebrow="读者口碑"
              title="他们把这里当作信息起点"
              description="以下为读者在联系表单与邮件中留下的评价，已隐去真实身份信息。"
            />
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {testimonials.map((item) => (
                <figure key={item.id} className="flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5">
                  <div className="flex items-center gap-1 text-accent-600" aria-label={`${item.rating} 星评价`}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <span key={i} className="flex h-3.5 w-3.5 items-center justify-center">
                        <i className={i < item.rating ? 'ri-star-fill text-xs' : 'ri-star-line text-xs'} aria-hidden="true" />
                      </span>
                    ))}
                  </div>
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.quote}</blockquote>
                  <figcaption className="mt-4 flex items-center gap-3 border-t border-background-200 pt-4">
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                      {item.avatarText}
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-950">{item.name}</span>
                      <span className="block text-[11px] text-foreground-600">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-50 py-14 md:py-20">
          <div className="mx-auto max-w-shell px-4 md:px-6">
            <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
              <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
                  <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-700">
                    有线索想提供、有内容想更正，或者只是想聊聊，都可以通过下面的方式找到编辑部。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-foreground-700">
                    <span className="flex items-center gap-2">
                      <i className="ri-mail-line" aria-hidden="true" />
                      {siteInfo.email}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-inbox-archive-line" aria-hidden="true" />
                      {siteInfo.tipEmail}
                    </span>
                    <span className="flex items-center gap-2">
                      <i className="ri-time-line" aria-hidden="true" />
                      {siteInfo.workingHours}
                    </span>
                  </div>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  前往联系页
                  <i className="ri-arrow-right-line" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}